import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import './GymServices.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDumbbell,
  faMapMarkerAlt,
  faBoxOpen,
  faClock,
  faPhone,
  faCheck,
  faUser,
  faUsers,
  faEnvelope,
  faShieldAlt,
  faArrowLeft,
  faCheckCircle,
  faTimesCircle,
  faReceipt,
  faCalendarAlt,
  faClipboardList,
  faCity,
  faTicketAlt,
  faArrowRight,
  faDownload,
  faRotateRight,
  faCreditCard,
  faEye,
  faSpinner,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import paymentService from '../../services/paymentService';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Container } from 'react-bootstrap';
import gymService, {
  GymCenter,
  GymPackage,
  GymPayUData,
  GymVoucher,
} from '../../services/gymService';
import axiosInstance from '../../api/axiosInstance';
import { API_ENDPOINTS } from '../../api/config';
import { useAuth } from '../../context/AuthContext';
import BackButton from '../../components/BackButton/BackButton';

// Dependant type from the API
interface Dependant {
  id: number;
  name: string;
  relationship_name?: string;
  relationship?: string;
}

interface StatItem {
  icon: IconDefinition;
  color: string;
  value: string | number;
  label: string;
}

const STEP_LABELS = [
  'Select Vendor',
  'Booking Details',
  'Payment',
  'Confirmation',
];

// ── Bookings-history helpers ──
const bookingCellStyle: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid #eef2f6',
  fontSize: '0.88rem',
  color: '#334155',
  whiteSpace: 'nowrap',
};

const formatBookingDate = (value?: string): string => {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const bookingStatusLabel = (status: GymVoucher['status']): string => {
  const map: Record<GymVoucher['status'], string> = {
    active: 'Active',
    pending: 'Pending',
    cancelled: 'Cancelled',
    failed: 'Failed',
  };
  return map[status] || status;
};

const bookingStatusStyle = (status: GymVoucher['status']): React.CSSProperties => {
  const colors: Record<GymVoucher['status'], { bg: string; fg: string }> = {
    active: { bg: '#dcfce7', fg: '#15803d' },
    pending: { bg: '#fef9c3', fg: '#a16207' },
    cancelled: { bg: '#fee2e2', fg: '#b91c1c' },
    failed: { bg: '#fee2e2', fg: '#b91c1c' },
  };
  const c = colors[status] || { bg: '#e2e8f0', fg: '#475569' };
  return {
    background: c.bg,
    color: c.fg,
    padding: '3px 10px',
    borderRadius: '999px',
    fontSize: '0.78rem',
    fontWeight: 600,
  };
};

const GymServices: React.FC = () => {
  const { t } = useTranslation(['services', 'common']);
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Step state
  const [step, setStep] = useState(1);

  // Location
  const [selectedLocation, setSelectedLocation] = useState('');

  // API Data
  const [gymCenters, setGymCenters] = useState<GymCenter[]>([]);
  const [packages, setPackages] = useState<GymPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Step 1 selections
  const [selectedCenter, setSelectedCenter] = useState<GymCenter | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<GymPackage | null>(null);

  // Packages dialog
  const [showPackagesDialog, setShowPackagesDialog] = useState(false);

  // Step 2 booking details
  const [bookingFor, setBookingFor] = useState<'self' | 'dependant'>('self');
  const [dependants, setDependants] = useState<Dependant[]>([]);
  const [selectedDependant, setSelectedDependant] = useState<Dependant | null>(null);
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [dependantsLoading, setDependantsLoading] = useState(false);

  // Branch state
  const [branches, setBranches] = useState<any[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<any | null>(null);
  const [branchesLoading, setBranchesLoading] = useState(false);

  // Step 3 payment
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [payuData, setPayuData] = useState<GymPayUData | null>(null);
  const payuFormRef = useRef<HTMLFormElement>(null);

  // Step 4 confirmation
  const [confirmationVoucher, setConfirmationVoucher] = useState<GymVoucher | null>(null);
  const [bookings, setBookings] = useState<GymVoucher[]>([]);
  const [showBookings, setShowBookings] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);

  const [selectedBookingDetail, setSelectedBookingDetail] = useState<GymVoucher | null>(null);
  const [invoiceDownloadingId, setInvoiceDownloadingId] = useState<number | null>(null);
  const [resumingBookingId, setResumingBookingId] = useState<number | null>(null);

  const handleDownloadInvoice = async (booking: GymVoucher) => {
    if (!booking.payment_txn_id) {
      setError('Invoice is not available for this booking yet.');
      return;
    }
    setInvoiceDownloadingId(booking.id);
    try {
      await paymentService.downloadInvoice(booking.payment_txn_id);
    } catch (err: any) {
      console.error('Invoice download failed:', err);
      setError(err.response?.data?.detail || 'Failed to download invoice. Please try again.');
    } finally {
      setInvoiceDownloadingId(null);
    }
  };

  const handleResumeBooking = async (booking: GymVoucher) => {
    if (!booking.voucher_uuid) {
      setError('Cannot resume this booking automatically. Please start a fresh booking.');
      return;
    }
    setResumingBookingId(booking.id);
    setError(null);
    try {
      const response = await gymService.resumePayment(booking.voucher_uuid);
      setPayuData(response.payu_data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      console.error('Resume payment failed:', err);
      setError(err.response?.data?.error || 'Failed to resume payment. Please try again.');
    } finally {
      setResumingBookingId(null);
    }
  };

  const bookingActionBtn: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: '0.76rem',
    fontWeight: 600,
    border: '1px solid #cbd5e1',
    background: '#fff',
    color: '#0f172a',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const renderBookingActions = (b: GymVoucher): React.ReactNode => {
    const buttons: React.ReactNode[] = [];
    buttons.push(
      <button
        key="details"
        type="button"
        style={bookingActionBtn}
        onClick={() => setSelectedBookingDetail(b)}
        title="View full booking details"
      >
        <FontAwesomeIcon icon={faEye} /> Details
      </button>,
    );
    if (b.status === 'active') {
      buttons.push(
        <button
          key="invoice"
          type="button"
          style={{ ...bookingActionBtn, background: '#ecfdf5', borderColor: '#a7f3d0', color: '#065f46' }}
          onClick={() => handleDownloadInvoice(b)}
          disabled={!b.payment_txn_id || invoiceDownloadingId === b.id}
          title={b.payment_txn_id ? 'Download payment invoice' : 'Invoice not available'}
        >
          <FontAwesomeIcon icon={invoiceDownloadingId === b.id ? faSpinner : faDownload} spin={invoiceDownloadingId === b.id} /> Invoice
        </button>,
      );
    } else if (b.status === 'pending') {
      buttons.push(
        <button
          key="resume"
          type="button"
          style={{ ...bookingActionBtn, background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' }}
          onClick={() => handleResumeBooking(b)}
          disabled={resumingBookingId === b.id}
          title="Complete or resume this payment"
        >
          <FontAwesomeIcon icon={resumingBookingId === b.id ? faSpinner : faCreditCard} spin={resumingBookingId === b.id} /> Resume Payment
        </button>,
      );
    } else if (b.status === 'failed' || b.status === 'cancelled') {
      buttons.push(
        <button
          key="retry"
          type="button"
          style={{ ...bookingActionBtn, background: '#fef3c7', borderColor: '#fcd34d', color: '#92400e' }}
          onClick={() => handleResumeBooking(b)}
          disabled={resumingBookingId === b.id}
          title="Retry payment for the same package"
        >
          <FontAwesomeIcon icon={resumingBookingId === b.id ? faSpinner : faRotateRight} spin={resumingBookingId === b.id} /> Retry
        </button>,
      );
    }
    return <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{buttons}</div>;
  };

  const subtitle = t('services:gym.subtitle');

  // Check URL params for payment callback on mount
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const voucherUuid = searchParams.get('voucher');

    if (paymentStatus === 'success' && voucherUuid) {
      setStep(4);
      fetchVoucherByUuid(voucherUuid);
      // Replace history so back button doesn't go to PayU
      window.history.replaceState({}, '', window.location.pathname + '?payment=success&voucher=' + voucherUuid);
    } else if (paymentStatus === 'failed') {
      setStep(4);
      // Replace history so back button doesn't go to PayU
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);

  // Fetch gym centers + packages on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [centersData, packagesData] = await Promise.all([
          gymService.getCenters(),
          gymService.getPackages(),
        ]);
        setGymCenters(centersData);
        setPackages(packagesData);

        const cities = Array.from(new Set(centersData.map((c) => c.city).filter(Boolean)));
        if (cities.length > 0 && !selectedLocation) {
          setSelectedLocation(cities[0] as string);
        }
      } catch (err) {
        console.error('Error fetching gym data:', err);
        setError(t('services:gym.failedToLoadCenters'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch the user's gym bookings for the stats tile + history view
  // (re-runs after a payment redirect so a new booking is reflected)
  useEffect(() => {
    gymService.getVouchers()
      .then((vouchers) => setBookings(Array.isArray(vouchers) ? vouchers : []))
      .catch((err) => {
        console.error('Failed to fetch gym bookings:', err);
        setBookings([]);
      });
  }, [searchParams]);

  // Pre-fill contact details from user profile
  useEffect(() => {
    if (user) {
      setContactEmail(user.email || '');
      setContactPhone(user.mobile_number || '');
    }
  }, [user]);

  // Auto-submit PayU form when payuData is set
  useEffect(() => {
    if (payuData && payuFormRef.current) {
      setTimeout(() => {
        payuFormRef.current?.submit();
      }, 100);
    }
  }, [payuData]);

  // Fetch dependants when booking for dependant
  useEffect(() => {
    if (bookingFor === 'dependant' && dependants.length === 0) {
      fetchDependants();
    }
  }, [bookingFor]);

  // Fetch branches when a center is selected
  useEffect(() => {
    if (selectedCenter) {
      setBranchesLoading(true);
      axiosInstance.get(API_ENDPOINTS.gym.branches, { params: { gym_center: selectedCenter.id } })
        .then(res => {
          const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
          setBranches(data);
          // Auto-select if only one branch
          if (data.length === 1) setSelectedBranch(data[0]);
        })
        .catch(() => setBranches([]))
        .finally(() => setBranchesLoading(false));
    } else {
      setBranches([]);
      setSelectedBranch(null);
    }
  }, [selectedCenter]);

  const fetchDependants = async () => {
    try {
      setDependantsLoading(true);
      const res = await axiosInstance.get(API_ENDPOINTS.dependants.list);
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      setDependants(data);
    } catch (err) {
      console.error('Failed to fetch dependants:', err);
      setDependants([]);
    } finally {
      setDependantsLoading(false);
    }
  };

  const fetchVoucherByUuid = async (uuid: string) => {
    try {
      setConfirmationLoading(true);
      const vouchers = await gymService.getVouchers();
      const found = vouchers.find(
        (v) => v.voucher_uuid === uuid
      );
      if (found) {
        setConfirmationVoucher(found);
      }
    } catch (err) {
      console.error('Failed to fetch voucher details:', err);
    } finally {
      setConfirmationLoading(false);
    }
  };

  // Derived data
  const locations = Array.from(
    new Set(gymCenters.map((c) => c.city).filter(Boolean))
  ) as string[];
  const filteredCenters = gymCenters.filter((c) => c.city === selectedLocation);
  const centerPackages = selectedCenter
    ? packages.filter(() => true) // Show all packages; backend may filter by center
    : [];

  const stats: StatItem[] = [
    { icon: faClipboardList, color: '#06b6d4', value: gymCenters.length, label: t('services:gym.totalCenters') },
    { icon: faBoxOpen, color: '#8b5cf6', value: packages.length, label: t('services:gym.totalPackages') },
    { icon: faCity, color: '#10b981', value: locations.length, label: t('services:gym.cities') },
    { icon: faTicketAlt, color: '#f59e0b', value: bookings.length, label: t('services:gym.yourBookings') },
  ];

  const paymentStatus = searchParams.get('payment');
  const finalAmount = selectedPackage
    ? selectedPackage.discounted_price || selectedPackage.original_price
    : 0;

  // Handlers
  const handleLocationChange = (location: string) => {
    setSelectedLocation(location);
    setSelectedCenter(null);
    setSelectedPackage(null);
  };

  const handleContinueToStep2 = () => {
    if (selectedCenter && selectedPackage) {
      setStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBackToStep1 = () => {
    setStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleProceedToPayment = () => {
    if (bookingFor === 'dependant' && !selectedDependant) {
      return;
    }
    setStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToStep2 = () => {
    setStep(2);
    setPayuData(null);
    setPaymentLoading(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInitiatePayment = async () => {
    if (!selectedCenter || !selectedPackage) return;

    try {
      setPaymentLoading(true);
      setError(null);

      const payload: any = {
        package_id: selectedPackage.id,
        gym_center_id: selectedCenter.id,
        branch_id: selectedBranch?.id || null,
        booking_for: bookingFor,
      };

      if (bookingFor === 'dependant' && selectedDependant) {
        payload.dependant_id = selectedDependant.id;
      }

      const response = await gymService.initiatePayment(payload);
      setPayuData(response.payu_data);
    } catch (err: any) {
      console.error('Payment initiation failed:', err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Failed to initiate payment. Please try again.'
      );
      setPaymentLoading(false);
    }
  };

  const handleResetFlow = () => {
    setStep(1);
    setSelectedCenter(null);
    setSelectedPackage(null);
    setBookingFor('self');
    setSelectedDependant(null);
    setSelectedBranch(null);
    setPayuData(null);
    setPaymentLoading(false);
    setConfirmationVoucher(null);
    setError(null);
    // Clear URL params
    window.history.replaceState({}, '', window.location.pathname);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Loading state
  if (loading) {
    return (
      <div className="gym-services-page">
        <div className="gym-loading-container">
          <div className="gym-loading-spinner" />
          <p>{t('services:gym.loadingGymServices')}</p>
        </div>
      </div>
    );
  }

  // Error state (initial load)
  if (error && step === 1 && gymCenters.length === 0) {
    return (
      <div className="gym-services-page">
        <div className="gym-error-container">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>{t('services:gym.retry')}</button>
        </div>
      </div>
    );
  }

  // ============================
  // STEP PROGRESS INDICATOR
  // ============================
  const renderStepper = () => (
    <div className="gym-step-stepper">
      {STEP_LABELS.map((label, idx) => {
        const stepNum = idx + 1;
        const isActive = step === stepNum;
        const isCompleted = step > stepNum;
        return (
          <div
            key={stepNum}
            className={`gym-step-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
          >
            <div className="gym-step-circle">
              {isCompleted ? (
                <FontAwesomeIcon icon={faCheck} />
              ) : (
                stepNum
              )}
            </div>
            <span className="gym-step-label">{label}</span>
            {idx < STEP_LABELS.length - 1 && <div className="gym-step-connector" />}
          </div>
        );
      })}
    </div>
  );

  // ============================
  // STEP 1: BROWSE & SELECT
  // ============================
  const renderStep1 = () => (
    <>
      {/* Location Chips */}
      <Container>
        <div className="gym-location-chips">
          {locations.map((loc) => (
            <button
              key={loc}
              className={`gym-location-chip ${selectedLocation === loc ? 'active' : ''}`}
              onClick={() => handleLocationChange(loc)}
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              {loc}
            </button>
          ))}
        </div>
      </Container>

      {/* Gym Centers */}
      {filteredCenters.length > 0 && (
        <section className="gym-content-section">
          <Container>
            <div className="gym-section-header">
              <h2 className="gym-section-title">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="gym-section-title-icon" style={{ color: '#06b6d4' }} />
                {t('services:gym.availableCentersIn', { location: selectedLocation })}
              </h2>
              <p className="gym-section-subtitle">{t('services:gym.selectCenterSubtitle')}</p>
            </div>
            <div className="gym-centers-grid">
              {filteredCenters.map((center) => (
                <div
                  key={center.id}
                  className="gym-center-card"
                  onClick={() => {
                    setSelectedCenter(center);
                    setShowPackagesDialog(true);
                  }}
                >
                  <div className="gym-vendor-img">
                    {center.logo ? (
                      <img src={center.logo} alt={center.name} />
                    ) : (
                      <div className="gym-vendor-img-placeholder">
                        <FontAwesomeIcon icon={faDumbbell} />
                      </div>
                    )}
                    <div className="gym-vendor-img-overlay" />
                  </div>
                  <div className="gym-vendor-body">
                    <h4>{center.name}</h4>
                    {center.city && (
                      <p className="gym-center-city">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="gym-center-detail-icon" />
                        {center.city}{(center as any).state ? `, ${(center as any).state}` : ''}
                      </p>
                    )}
                    {center.phone && (
                      <p className="gym-center-detail">
                        <FontAwesomeIcon icon={faPhone} className="gym-center-detail-icon" />
                        {center.phone}
                      </p>
                    )}
                    <button className="gym-vendor-view-btn">
                      {t('services:gym.viewPackages')}
                      <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* Packages Dialog */}
      {showPackagesDialog && selectedCenter && (
        <div className="gym-packages-dialog-overlay" onClick={() => setShowPackagesDialog(false)}>
          <div className="gym-packages-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="gym-packages-dialog-header">
              <div>
                <h3>{selectedCenter.name}</h3>
                <p>{selectedCenter.city}</p>
              </div>
              <button className="gym-packages-dialog-close" onClick={() => setShowPackagesDialog(false)}>
                &#10005;
              </button>
            </div>
            <div className="gym-packages-dialog-body">
              {centerPackages.length === 0 ? (
                <div className="gym-packages-dialog-empty">
                  <FontAwesomeIcon icon={faBoxOpen} />
                  <p>{t('services:gym.noPackagesForCenter')}</p>
                </div>
              ) : (
                <div className="gym-packages-dialog-grid">
                  {centerPackages.map((pkg) => (
                    <div key={pkg.id} className="gym-pkg-dialog-card">
                      <h4>{pkg.title}</h4>
                      <span className="gym-pkg-duration">
                        <FontAwesomeIcon icon={faClock} />
                        {pkg.duration_months} month(s)
                      </span>
                      <div className="gym-pkg-price-row">
                        <span className="gym-pkg-price-current">
                          ₹{pkg.discounted_price || pkg.original_price}
                        </span>
                        {pkg.discounted_price && pkg.discounted_price < pkg.original_price && (
                          <span className="gym-pkg-price-original">₹{pkg.original_price}</span>
                        )}
                        {pkg.discount_percent != null && pkg.discount_percent > 0 && (
                          <span style={{
                            display: 'inline-block',
                            background: '#10b981',
                            color: '#fff',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            borderRadius: '4px',
                            padding: '2px 7px',
                            marginLeft: '8px',
                            letterSpacing: '0.03em',
                          }}>
                            {pkg.discount_percent}% OFF
                          </span>
                        )}
                      </div>
                      {pkg.description && <p className="gym-pkg-features">{pkg.description}</p>}
                      <button
                        className="gym-pkg-select-btn"
                        onClick={() => {
                          setSelectedPackage(pkg);
                          setShowPackagesDialog(false);
                          setStep(2);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        {t('services:gym.selectPackage')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

  // ============================
  // STEP 2: BOOKING DETAILS
  // ============================
  const renderStep2 = () => (
    <Container>
      <div className="gym-step-content">
        <button className="gym-step-back-btn" onClick={handleBackToStep1}>
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('services:gym.backToSelection')}
        </button>

        {/* Selected Summary Card */}
        <div className="gym-step-summary-card">
          <div className="gym-step-summary-accent" />
          <h3 className="gym-step-summary-title">{t('services:gym.yourSelection')}</h3>
          <div className="gym-step-summary-grid">
            <div className="gym-step-summary-item">
              <span className="gym-step-summary-label">{t('services:gym.center')}</span>
              <span className="gym-step-summary-value">{selectedCenter?.name}</span>
              {selectedCenter?.city && (
                <span className="gym-step-summary-sub">
                  <FontAwesomeIcon icon={faMapMarkerAlt} /> {selectedCenter.city}
                </span>
              )}
            </div>
            <div className="gym-step-summary-item">
              <span className="gym-step-summary-label">{t('services:gym.package')}</span>
              <span className="gym-step-summary-value">{selectedPackage?.title}</span>
              <span className="gym-step-summary-sub">
                {selectedPackage?.duration_months} month(s) &mdash; ₹{finalAmount}
              </span>
            </div>
            {selectedBranch && (
              <div className="gym-step-summary-item">
                <span className="gym-step-summary-label">{t('services:gym.branch')}</span>
                <span className="gym-step-summary-value">{selectedBranch.name}</span>
                {selectedBranch.city && (
                  <span className="gym-step-summary-sub">
                    <FontAwesomeIcon icon={faMapMarkerAlt} /> {selectedBranch.city}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Select Branch */}
        <div className="gym-step-section-card">
          <h3 className="gym-step-section-title">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="gym-step-section-icon" />
            {t('services:gym.selectBranch')}
          </h3>
          {branchesLoading ? (
            <div className="gym-step-dependant-loading">
              <div className="gym-loading-spinner" style={{ width: 24, height: 24, borderWidth: 3 }} />
              <span>{t('services:gym.loadingBranches')}</span>
            </div>
          ) : branches.length === 0 ? (
            <div className="gym-step-dependant-empty">
              <p>{t('services:gym.noBranches')}</p>
            </div>
          ) : (
            <div className="gym-step-dependant-list">
              {branches.map((branch) => (
                <div
                  key={branch.id}
                  className={`gym-step-dependant-card ${selectedBranch?.id === branch.id ? 'active' : ''}`}
                  onClick={() => setSelectedBranch(branch)}
                >
                  <div className="gym-step-dependant-avatar">
                    <FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: '0.85rem' }} />
                  </div>
                  <div className="gym-step-dependant-info">
                    <span className="gym-step-dependant-name">{branch.name}</span>
                    {(branch.city || branch.address) && (
                      <span className="gym-step-dependant-rel">
                        {[branch.city, branch.address].filter(Boolean).join(' — ')}
                      </span>
                    )}
                    {branch.phone && (
                      <span className="gym-step-dependant-rel">
                        <FontAwesomeIcon icon={faPhone} style={{ marginRight: 4 }} />
                        {branch.phone}
                      </span>
                    )}
                  </div>
                  {selectedBranch?.id === branch.id && (
                    <FontAwesomeIcon icon={faCheckCircle} className="gym-step-dependant-check" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Who is this for? */}
        <div className="gym-step-section-card">
          <h3 className="gym-step-section-title">
            <FontAwesomeIcon icon={faUsers} className="gym-step-section-icon" />
            {t('services:gym.whoIsThisFor')}
          </h3>
          <div className="gym-step-radio-group">
            <label
              className={`gym-step-radio-card ${bookingFor === 'self' ? 'active' : ''}`}
              onClick={() => {
                setBookingFor('self');
                setSelectedDependant(null);
              }}
            >
              <div className="gym-step-radio-circle">
                {bookingFor === 'self' && <div className="gym-step-radio-dot" />}
              </div>
              <FontAwesomeIcon icon={faUser} className="gym-step-radio-icon" />
              <div>
                <span className="gym-step-radio-label">{t('services:gym.myself')}</span>
                <span className="gym-step-radio-desc">{t('services:gym.bookForYourself')}</span>
              </div>
            </label>
            <label
              className={`gym-step-radio-card ${bookingFor === 'dependant' ? 'active' : ''}`}
              onClick={() => setBookingFor('dependant')}
            >
              <div className="gym-step-radio-circle">
                {bookingFor === 'dependant' && <div className="gym-step-radio-dot" />}
              </div>
              <FontAwesomeIcon icon={faUsers} className="gym-step-radio-icon" />
              <div>
                <span className="gym-step-radio-label">{t('services:gym.familyMember')}</span>
                <span className="gym-step-radio-desc">{t('services:gym.bookForDependant')}</span>
              </div>
            </label>
          </div>

          {/* Dependant selector */}
          {bookingFor === 'dependant' && (
            <div className="gym-step-dependant-section">
              {dependantsLoading ? (
                <div className="gym-step-dependant-loading">
                  <div className="gym-loading-spinner" style={{ width: 24, height: 24, borderWidth: 3 }} />
                  <span>{t('services:gym.loadingFamilyMembers')}</span>
                </div>
              ) : dependants.length === 0 ? (
                <div className="gym-step-dependant-empty">
                  <p>{t('services:gym.noFamilyMembers')}</p>
                </div>
              ) : (
                <div className="gym-step-dependant-list">
                  {dependants.map((dep) => (
                    <div
                      key={dep.id}
                      className={`gym-step-dependant-card ${selectedDependant?.id === dep.id ? 'active' : ''}`}
                      onClick={() => setSelectedDependant(dep)}
                    >
                      <div className="gym-step-dependant-avatar">
                        {dep.name?.charAt(0)?.toUpperCase() || 'D'}
                      </div>
                      <div className="gym-step-dependant-info">
                        <span className="gym-step-dependant-name">{dep.name}</span>
                        {(dep.relationship_name || dep.relationship) && (
                          <span className="gym-step-dependant-rel">
                            {dep.relationship_name || dep.relationship}
                          </span>
                        )}
                      </div>
                      {selectedDependant?.id === dep.id && (
                        <FontAwesomeIcon icon={faCheckCircle} className="gym-step-dependant-check" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Contact Details */}
        <div className="gym-step-section-card">
          <h3 className="gym-step-section-title">
            <FontAwesomeIcon icon={faEnvelope} className="gym-step-section-icon" />
            {t('services:gym.contactDetails')}
          </h3>
          <div className="gym-step-form-grid">
            <div className="gym-step-form-field">
              <label className="gym-step-form-label">{t('services:gym.phoneNumber')}</label>
              <div className="gym-step-input-wrapper">
                <FontAwesomeIcon icon={faPhone} className="gym-step-input-icon" />
                <input
                  type="tel"
                  className="gym-step-input"
                  placeholder="Enter phone number"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </div>
            </div>
            <div className="gym-step-form-field">
              <label className="gym-step-form-label">Email Address</label>
              <div className="gym-step-input-wrapper">
                <FontAwesomeIcon icon={faEnvelope} className="gym-step-input-icon" />
                <input
                  type="email"
                  className="gym-step-input"
                  placeholder="Enter email address"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Proceed Button */}
        <div className="gym-book-container">
          <button
            className="gym-book-btn"
            onClick={handleProceedToPayment}
            disabled={bookingFor === 'dependant' && !selectedDependant}
          >
            PROCEED TO PAYMENT
          </button>
        </div>
      </div>
    </Container>
  );

  // ============================
  // STEP 3: PAYMENT SUMMARY
  // ============================
  const renderStep3 = () => (
    <Container>
      <div className="gym-step-content">
        <button className="gym-step-back-btn" onClick={handleBackToStep2}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Details
        </button>

        {/* Order Summary Card */}
        <div className="gym-step-order-card">
          <div className="gym-step-order-header">
            <FontAwesomeIcon icon={faReceipt} className="gym-step-order-header-icon" />
            <h3>Order Summary</h3>
          </div>
          <div className="gym-step-order-body">
            <div className="gym-step-order-row">
              <span className="gym-step-order-label">Gym Center</span>
              <span className="gym-step-order-value">
                {selectedCenter?.name}
                {selectedCenter?.city && (
                  <small className="gym-step-order-sub"> &mdash; {selectedCenter.city}</small>
                )}
              </span>
            </div>
            <div className="gym-step-order-row">
              <span className="gym-step-order-label">Package</span>
              <span className="gym-step-order-value">{selectedPackage?.title}</span>
            </div>
            <div className="gym-step-order-row">
              <span className="gym-step-order-label">Duration</span>
              <span className="gym-step-order-value">{selectedPackage?.duration_months} month(s)</span>
            </div>
            <div className="gym-step-order-row">
              <span className="gym-step-order-label">Branch</span>
              <span className="gym-step-order-value">{selectedBranch?.name || 'Not selected'}</span>
            </div>
            <div className="gym-step-order-row">
              <span className="gym-step-order-label">For</span>
              <span className="gym-step-order-value">
                {bookingFor === 'self' ? 'Self' : selectedDependant?.name || 'Dependant'}
              </span>
            </div>
            <div className="gym-step-order-divider" />
            <div className="gym-step-order-row gym-step-order-total">
              <span className="gym-step-order-label">Total Amount</span>
              <span className="gym-step-order-amount">₹{finalAmount}</span>
            </div>
          </div>
        </div>

        {/* How to Avail */}
        {selectedPackage?.how_to_use && (
          <div className="gym-step-order-card" style={{ marginTop: '1.25rem' }}>
            <div className="gym-step-order-header">
              <FontAwesomeIcon icon={faClipboardList} className="gym-step-order-header-icon" />
              <h3>How to Avail</h3>
            </div>
            <div className="gym-step-order-body">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {selectedPackage.how_to_use.split('\n').filter(line => line.trim()).map((line, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{
                        width: 32,
                        padding: '10px 12px 10px 0',
                        verticalAlign: 'top',
                        fontWeight: 700,
                        color: '#06b6d4',
                        fontSize: '0.85rem',
                        whiteSpace: 'nowrap',
                      }}>
                        {idx + 1}.
                      </td>
                      <td style={{
                        padding: '10px 0',
                        color: '#374151',
                        fontSize: '0.9rem',
                        lineHeight: 1.5,
                      }}>
                        {line.trim()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Secure payment badge */}
        <div className="gym-step-secure-badge">
          <FontAwesomeIcon icon={faShieldAlt} />
          <span>Secure payment powered by PayU</span>
        </div>

        {/* Error message */}
        {error && (
          <div className="gym-step-error-banner">
            <FontAwesomeIcon icon={faTimesCircle} />
            <span>{error}</span>
          </div>
        )}

        {/* Redirecting overlay */}
        {payuData && (
          <div className="gym-step-redirecting">
            <div className="gym-loading-spinner" />
            <p>Redirecting to payment gateway...</p>
          </div>
        )}

        {/* Pay Button */}
        <div className="gym-book-container">
          <button
            className="gym-book-btn gym-book-btn--pay"
            onClick={handleInitiatePayment}
            disabled={paymentLoading || !!payuData}
          >
            {paymentLoading ? 'PROCESSING...' : `PAY ₹${finalAmount}`}
          </button>
        </div>
      </div>
    </Container>
  );

  // ============================
  // STEP 4: CONFIRMATION
  // ============================
  const renderStep4 = () => {
    const isSuccess = paymentStatus === 'success';
    const isFailed = paymentStatus === 'failed';

    if (confirmationLoading) {
      return (
        <Container>
          <div className="gym-step-content">
            <div className="gym-step-confirmation-loading">
              <div className="gym-loading-spinner" />
              <p>Fetching your booking details...</p>
            </div>
          </div>
        </Container>
      );
    }

    if (isSuccess) {
      return (
        <Container>
          <div className="gym-step-content">
            <div className="gym-step-confirmation-card gym-step-confirmation--success">
              <div className="gym-step-confirmation-icon-wrap gym-step-confirmation-icon--success">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <h2 className="gym-step-confirmation-title">Booking Confirmed!</h2>
              <p className="gym-step-confirmation-subtitle">
                Your gym membership has been successfully booked and paid for.
              </p>

              {confirmationVoucher && (
                <div className="gym-step-voucher-details">
                  <div className="gym-step-voucher-row">
                    <span className="gym-step-voucher-label">Voucher ID</span>
                    <span className="gym-step-voucher-value gym-step-voucher-id">
                      {confirmationVoucher.voucher_id}
                    </span>
                  </div>
                  <div className="gym-step-voucher-row">
                    <span className="gym-step-voucher-label">Gym Center</span>
                    <span className="gym-step-voucher-value">
                      {confirmationVoucher.gym_center_name || 'N/A'}
                    </span>
                  </div>
                  <div className="gym-step-voucher-row">
                    <span className="gym-step-voucher-label">Package</span>
                    <span className="gym-step-voucher-value">
                      {confirmationVoucher.package_title || 'N/A'}
                    </span>
                  </div>
                  <div className="gym-step-voucher-row">
                    <span className="gym-step-voucher-label">Duration</span>
                    <span className="gym-step-voucher-value">
                      {confirmationVoucher.selected_package || 'N/A'}
                    </span>
                  </div>
                  {confirmationVoucher.start_date && (
                    <div className="gym-step-voucher-row">
                      <span className="gym-step-voucher-label">
                        <FontAwesomeIcon icon={faCalendarAlt} /> Start Date
                      </span>
                      <span className="gym-step-voucher-value">
                        {new Date(confirmationVoucher.start_date).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                  )}
                  {confirmationVoucher.end_date && (
                    <div className="gym-step-voucher-row">
                      <span className="gym-step-voucher-label">
                        <FontAwesomeIcon icon={faCalendarAlt} /> End Date
                      </span>
                      <span className="gym-step-voucher-value">
                        {new Date(confirmationVoucher.end_date).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                  )}
                  <div className="gym-step-voucher-row">
                    <span className="gym-step-voucher-label">Amount Paid</span>
                    <span className="gym-step-voucher-value gym-step-voucher-amount">
                      ₹{confirmationVoucher.amount_paid || 'N/A'}
                    </span>
                  </div>
                </div>
              )}

              <button className="gym-book-btn" onClick={handleResetFlow}>
                BACK TO GYM SERVICES
              </button>
            </div>
          </div>
        </Container>
      );
    }

    if (isFailed) {
      return (
        <Container>
          <div className="gym-step-content">
            <div className="gym-step-confirmation-card gym-step-confirmation--failed">
              <div className="gym-step-confirmation-icon-wrap gym-step-confirmation-icon--failed">
                <FontAwesomeIcon icon={faTimesCircle} />
              </div>
              <h2 className="gym-step-confirmation-title">Payment Failed</h2>
              <p className="gym-step-confirmation-subtitle">
                We were unable to process your payment. No charges have been made to your account.
              </p>
              <button className="gym-book-btn" onClick={handleResetFlow}>
                TRY AGAIN
              </button>
            </div>
          </div>
        </Container>
      );
    }

    // Fallback — shouldn't normally be seen
    return (
      <Container>
        <div className="gym-step-content">
          <div className="gym-step-confirmation-card">
            <p>Processing your request...</p>
            <button className="gym-book-btn" onClick={handleResetFlow}>
              BACK TO GYM SERVICES
            </button>
          </div>
        </div>
      </Container>
    );
  };

  return (
    <div className="gym-services-page">
      {/* ============ WELCOME BANNER ============ */}
      <section className="gym-welcome-banner">
        <Container>
          <BackButton />
          <div className="gym-welcome-layout">
            <div className="gym-welcome-text">
              <h1 className="gym-welcome-title">
                <FontAwesomeIcon icon={faDumbbell} />
                Gym & Fitness
              </h1>
              <p className="gym-welcome-subtitle">
                {subtitle}
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* ============ STATS ROW ============ */}
      <section className="gym-stats-section">
        <Container>
          <div className="gym-stats-row">
            {stats.map((item, idx) => {
              const isBookings = item.label === t('services:gym.yourBookings');
              return (
                <div
                  className="gym-stat-card"
                  key={idx}
                  onClick={isBookings ? () => setShowBookings((s) => !s) : undefined}
                  style={isBookings ? { cursor: 'pointer' } : undefined}
                  title={isBookings ? t('services:gym.viewBookings') || 'View my bookings' : undefined}
                >
                  <div className="gym-stat-icon" style={{ background: `${item.color}12`, color: item.color }}>
                    <FontAwesomeIcon icon={item.icon} />
                  </div>
                  <div className="gym-stat-info">
                    <span className="gym-stat-value">{item.value}</span>
                    <span className="gym-stat-label">{item.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ============ MY BOOKINGS (history) ============ */}
      {showBookings && (
        <section className="gym-content-section">
          <Container>
            <div className="gym-section-header">
              <h2 className="gym-section-title">
                <FontAwesomeIcon icon={faTicketAlt} className="gym-section-title-icon" style={{ color: '#f59e0b' }} />
                {t('services:gym.yourBookings')}
              </h2>
            </div>
            {bookings.length === 0 ? (
              <p className="gym-section-subtitle">{t('services:gym.noBookings') || 'You have no gym bookings yet.'}</p>
            ) : (
              <div className="gym-bookings-table-wrap" style={{ overflowX: 'auto' }}>
                <table className="gym-bookings-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Gym Name', 'Package', 'Voucher ID', 'Booking Date', 'Start Date', 'End Date', 'Amount', 'Status', 'Actions'].map((h) => (
                        <th key={h} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: '0.82rem', color: '#64748b', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.id}>
                        <td style={bookingCellStyle}>{b.gym_center_name}{b.gym_center_city ? `, ${b.gym_center_city}` : ''}</td>
                        <td style={bookingCellStyle}>{b.package_title}</td>
                        <td style={{ ...bookingCellStyle, fontFamily: 'monospace' }}>{b.voucher_id}</td>
                        <td style={bookingCellStyle}>{formatBookingDate(b.voucher_datetime)}</td>
                        <td style={bookingCellStyle}>{formatBookingDate(b.start_date)}</td>
                        <td style={bookingCellStyle}>{formatBookingDate(b.end_date)}</td>
                        <td style={bookingCellStyle}>₹{b.amount_paid}</td>
                        <td style={bookingCellStyle}>
                          <span style={{ ...bookingStatusStyle(b.status) }}>{bookingStatusLabel(b.status)}</span>
                        </td>
                        <td style={bookingCellStyle}>{renderBookingActions(b)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {selectedBookingDetail && (
                  <div className="gym-booking-detail-modal" onClick={() => setSelectedBookingDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 16 }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 520, width: '100%', boxShadow: '0 24px 48px rgba(15,23,42,0.2)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <div>
                          <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#0f172a' }}>Booking Details</h3>
                          <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '0.95rem' }}>
                            Review the voucher and payment information for this booking.
                          </p>
                        </div>
                        <button onClick={() => setSelectedBookingDetail(null)} style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1.25rem', lineHeight: 1 }}>
                          <FontAwesomeIcon icon={faTimesCircle} />
                        </button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 1fr) minmax(180px, 1.3fr)', rowGap: 12, columnGap: 16, fontSize: '0.92rem', color: '#334155' }}>
                        <span style={{ color: '#475569', fontWeight: 600 }}>Voucher ID</span>
                        <span style={{ fontFamily: 'monospace', color: '#0f172a', fontWeight: 700 }}>{selectedBookingDetail.voucher_id}</span>
                        <span style={{ color: '#475569', fontWeight: 600 }}>Gym</span>
                        <span>{selectedBookingDetail.gym_center_name}{selectedBookingDetail.gym_center_city ? `, ${selectedBookingDetail.gym_center_city}` : ''}</span>
                        <span style={{ color: '#475569', fontWeight: 600 }}>Package</span>
                        <span>{selectedBookingDetail.package_title}</span>
                        <span style={{ color: '#475569', fontWeight: 600 }}>Booking For</span>
                        <span style={{ textTransform: 'capitalize' }}>{selectedBookingDetail.booking_for}</span>
                        <span style={{ color: '#475569', fontWeight: 600 }}>Booked On</span>
                        <span>{formatBookingDate(selectedBookingDetail.voucher_datetime)}</span>
                        <span style={{ color: '#475569', fontWeight: 600 }}>Valid From</span>
                        <span>{formatBookingDate(selectedBookingDetail.start_date)}</span>
                        <span style={{ color: '#475569', fontWeight: 600 }}>Valid Until</span>
                        <span>{formatBookingDate(selectedBookingDetail.end_date)}</span>
                        <span style={{ color: '#475569', fontWeight: 600 }}>Amount Paid</span>
                        <span style={{ color: '#059669', fontWeight: 700 }}>₹{selectedBookingDetail.amount_paid}</span>
                        <span style={{ color: '#475569', fontWeight: 600 }}>Payment Ref</span>
                        <span style={{ fontFamily: 'monospace', color: '#334155' }}>{selectedBookingDetail.payment_txn_id || '—'}</span>
                        <span style={{ color: '#475569', fontWeight: 600 }}>Status</span>
                        <span><span style={bookingStatusStyle(selectedBookingDetail.status)}>{bookingStatusLabel(selectedBookingDetail.status)}</span></span>
                        {(selectedBookingDetail.status === 'failed' || selectedBookingDetail.status === 'cancelled') && (
                          <>
                            <span style={{ color: '#475569', fontWeight: 600 }}>Reason</span>
                            <span style={{ color: '#b91c1c' }}>
                              {selectedBookingDetail.status === 'cancelled'
                                ? 'The payment was cancelled before it completed.'
                                : 'The payment was declined or timed out on the gateway.'}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Container>
        </section>
      )}

      {/* ============ STEP PROGRESS BAR ============ */}
      <Container>
        {renderStepper()}
      </Container>

      {/* ============ STEP CONTENT ============ */}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}

      {/* ============ HIDDEN PAYU FORM ============ */}
      {payuData && (
        <form
          ref={payuFormRef}
          method="POST"
          action={`${payuData.payu_base_url}/_payment`}
          style={{ display: 'none' }}
        >
          <input type="hidden" name="key" value={payuData.key} />
          <input type="hidden" name="txnid" value={payuData.txnid} />
          <input type="hidden" name="amount" value={payuData.amount} />
          <input type="hidden" name="productinfo" value={payuData.productinfo} />
          <input type="hidden" name="firstname" value={payuData.firstname} />
          <input type="hidden" name="email" value={payuData.email} />
          <input type="hidden" name="phone" value={payuData.phone} />
          <input type="hidden" name="surl" value={payuData.surl} />
          <input type="hidden" name="furl" value={payuData.furl} />
          <input type="hidden" name="hash" value={payuData.hash} />
          <input type="hidden" name="service_provider" value={payuData.service_provider} />
        </form>
      )}
    </div>
  );
};

export default GymServices;
